import { CommonModule } from '@angular/common';
import { Component, inject, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@seahorse/common';
import {
  SeahorseModule,
  UiModuleOverviewConfiguration,
} from '@seahorse/domain';
import { ShButtonComponent } from '@seahorse/components';
import {
  DIALOG_DATA,
  DialogContent,
  DialogFooterComponent,
  DialogRef,
} from '@seahorse/shell';
import { UiModuleConfigurationOverviewDataService } from '../../../ui-modules/services/ui-modules-configuration-overview.service';
import { UiModuleOverviewConfigurationFormComponent } from '../../../ui-modules/components/ui-module-overview-configuration-form/ui-module-overview-configuration-form.component';
import { isOverviewConfigurationValid } from '../../../ui-modules/components/ui-module-overview-configuration-form/ui-module-overview-configuration-form.model';

/** What the dialog is opened with: the configuration to edit, none to add one. */
export interface SeahorseModuleOverviewConfigurationDialogData {
  seahorseModule: SeahorseModule;
  configuration: UiModuleOverviewConfiguration | null;
}

/**
 * The body of the overview configuration dialog of a Seahorse module
 * (`DialogService.open`): the overview configuration form and the save. It
 * edits a copy and saves it itself (update, or add for a module that has
 * none yet); the dialog closes with `ok` and the saved configuration, or
 * cancels (with an error message) when the save gives no result.
 */
@Component({
  selector: 'su-seahorse-module-overview-configuration-dialog',
  templateUrl: './seahorse-module-overview-configuration-dialog.component.html',
  imports: [
    CommonModule,
    DialogFooterComponent,
    ShButtonComponent,
    UiModuleOverviewConfigurationFormComponent,
  ],
})
export class SeahorseModuleOverviewConfigurationDialogComponent
  implements
    DialogContent<
      SeahorseModuleOverviewConfigurationDialogData,
      UiModuleOverviewConfiguration
    >
{
  readonly data =
    inject<SeahorseModuleOverviewConfigurationDialogData>(DIALOG_DATA);
  readonly dialogRef =
    inject<DialogRef<UiModuleOverviewConfiguration>>(DialogRef);

  @ViewChild(UiModuleOverviewConfigurationFormComponent)
  form?: UiModuleOverviewConfigurationFormComponent;

  private readonly _configurationService = inject(
    UiModuleConfigurationOverviewDataService
  );
  private readonly _notificationService = inject(NotificationService);
  private readonly _translateService = inject(TranslateService);

  readonly seahorseModule = this.data.seahorseModule;
  readonly isNew = !this.data.configuration;
  readonly configuration: UiModuleOverviewConfiguration = this.isNew
    ? UiModuleOverviewConfiguration.default()
    : JSON.parse(JSON.stringify(this.data.configuration));

  saveStarted = false;

  get isValid(): boolean {
    return isOverviewConfigurationValid(this.configuration);
  }

  save() {
    if (!this.isValid || this.saveStarted) {
      return;
    }

    this.saveStarted = true;
    this.form?.prepareForSave();

    const request = this.isNew
      ? this._configurationService.addForSeahorseModule(
          this.seahorseModule.id,
          this.configuration
        )
      : this._configurationService.updateOverviewConfiguration(
          this.configuration
        );

    request.subscribe({
      next: (response) => {
        this.saveStarted = false;

        if (response.result) {
          this.dialogRef.ok(response.result);
          this._notificationService.showSuccess(
            this._translateService.instant(
              this.isNew ? 'shared.terms.success' : 'shared.terms.updateSuccess'
            )
          );
        } else {
          this.dialogRef.cancel();
          this._notificationService.showError(
            this._translateService.instant('shared.terms.failed')
          );
        }
      },
      error: () => {
        this.saveStarted = false;
        this._notificationService.showError(
          this._translateService.instant('shared.terms.failed')
        );
      },
    });
  }
}
